"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

interface Testimonial {
  name: string;
  role: string;
  content: string;
  rating: number;
  location?: string;
}

interface InfiniteTestimonialScrollProps {
  testimonials: Testimonial[];
}

export function InfiniteTestimonialScroll({ testimonials }: InfiniteTestimonialScrollProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const testimonialGroups = [false, true] as const;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <section aria-labelledby="learner-proof-heading" className="w-full py-10 md:py-14">
      <div className="mx-auto mb-6 max-w-6xl px-4 text-center md:mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#FD954F]">
          Learner stories
        </p>
        <h2 className="mt-2 text-2xl font-bold text-white" id="learner-proof-heading">
          What changed when they started speaking
        </h2>
      </div>

      <div
        aria-label="Learner testimonial carousel"
        className="relative px-4 md:hidden"
        role="group"
      >
        <div className="mx-auto max-w-md">
          <TestimonialCard testimonial={testimonials[currentIndex]} />

          <div className="mt-5 flex items-center justify-center gap-4">
            <button
              aria-label="Previous testimonial"
              className="flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-white/10 bg-[#15161B] p-2 transition-colors hover:bg-white/10"
              onClick={handlePrev}
              type="button"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  aria-current={index === currentIndex ? "true" : undefined}
                  aria-label={`Go to testimonial ${index + 1}`}
                  className="flex min-h-11 min-w-11 items-center justify-center"
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  type="button"
                >
                  <span
                    aria-hidden="true"
                    className={`h-2 rounded-full transition-all ${
                      index === currentIndex ? "w-6 bg-[#FD954F]" : "w-2 bg-white/20"
                    }`}
                  />
                </button>
              ))}
            </div>

            <button
              aria-label="Next testimonial"
              className="flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-white/10 bg-[#15161B] p-2 transition-colors hover:bg-white/10"
              onClick={handleNext}
              type="button"
            >
              <ChevronRight className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto hidden max-w-6xl px-4 md:block">
        <div
          aria-label="Scrolling learner testimonials"
          className="testimonial-marquee relative overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14]"
          role="group"
          tabIndex={0}
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-[#0F0F14] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-[#0F0F14] to-transparent" />

          <div className="testimonial-marquee-track flex w-max items-stretch">
            {testimonialGroups.map((isDuplicate) => (
              <div
                aria-hidden={isDuplicate ? "true" : undefined}
                className="testimonial-marquee-group flex shrink-0 items-stretch gap-5 pr-5"
                key={isDuplicate ? "duplicate" : "primary"}
              >
                {testimonials.map((testimonial) => (
                  <div
                    className="testimonial-marquee-card flex w-[360px] shrink-0"
                    key={`${isDuplicate ? "duplicate" : "primary"}-${testimonial.name}-${testimonial.role}`}
                  >
                    <TestimonialCard testimonial={testimonial} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <style jsx global>{`
          @keyframes testimonial-scroll-left {
            from {
              transform: translate3d(0, 0, 0);
            }
            to {
              transform: translate3d(-50%, 0, 0);
            }
          }

          .testimonial-marquee-track {
            animation: testimonial-scroll-left 40s linear infinite;
            will-change: transform;
          }

          .testimonial-marquee:hover .testimonial-marquee-track,
          .testimonial-marquee:focus-within .testimonial-marquee-track {
            animation-play-state: paused;
          }

          @media (prefers-reduced-motion: reduce) {
            .testimonial-marquee-track {
              animation: none;
              display: block;
              transform: none;
              width: 100%;
            }

            .testimonial-marquee-group:first-child {
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 1.25rem;
              padding-right: 0;
              width: 100%;
            }

            .testimonial-marquee-group[aria-hidden="true"] {
              display: none;
            }

            .testimonial-marquee-card {
              width: auto;
            }
          }
        `}</style>
      </div>
    </section>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <article className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#15161B] p-6">
      <div className="mb-3 flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <span className="text-[#FD954F]" key={i}>
            {i < testimonial.rating ? "★" : "☆"}
          </span>
        ))}
      </div>

      <p className="mb-6 flex-grow text-sm leading-7 text-white/85">"{testimonial.content}"</p>

      <div>
        <p className="text-sm font-semibold text-white">{testimonial.name}</p>
        <p className="mt-1 text-xs text-[#AEAEB2]">
          {testimonial.role}
          {testimonial.location ? ` · ${testimonial.location}` : ""}
        </p>
      </div>
    </article>
  );
}
